import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { env } from '@/app/config/env';
import { ThemeProvider } from '@/app/theme/theme-provider';
import { AuthProvider } from '@/features/auth';

import { AppErrorBoundary } from './error-boundary';
import { queryClient } from './query-client';

/**
 * Every application-wide provider, in one place and in a deliberate order.
 *
 * Order matters:
 *   ErrorBoundary  outermost, so it can catch a provider that throws
 *   Theme          before anything that reads theme (the Toaster does)
 *   QueryClient    before Auth, which uses mutations
 *   Auth           configures the API client; must precede any request
 *   Tooltip        a single delay-group for the whole app
 *
 * Kept to five. Each one is here because something genuinely global needs it,
 * not as a place to hang state (§4 of Phase 4).
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider delayDuration={300}>
              {children}
              <Toaster />
              {!env.isProduction && <ReactQueryDevtools initialIsOpen={false} />}
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
